import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LOGO_URL, NAV_LINKS } from "@/components/landing/content";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-white/75 border-b border-slate-200/60"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 md:px-8 h-16 md:h-20 flex items-center justify-between">
        <a
          href="#top"
          data-testid="nav-logo"
          className="flex items-center gap-3"
        >
          <img
            src={LOGO_URL}
            alt="JT Brand Scales"
            className="h-10 w-10 md:h-11 md:w-11 rounded-xl object-cover ring-1 ring-slate-200"
          />
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="font-display font-extrabold text-slate-900 text-base">
              JT Brand Scales
            </span>
            <span className="text-[11px] uppercase tracking-[0.18em] text-slate-500">
              Kentucky · Marketing Agency
            </span>
          </div>
        </a>

        <nav className="hidden md:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.testid}
              className="px-3 py-2 rounded-full text-sm font-medium text-slate-700 hover:text-slate-900 hover:bg-slate-100 transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Button
            asChild
            data-testid="nav-cta-quote"
            className="hidden sm:inline-flex rounded-full bg-orange-600 hover:bg-orange-700 text-white font-semibold px-5 btn-shine"
          >
            <a href="#contact">Get free quote</a>
          </Button>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded-full border border-slate-200 text-slate-800"
            aria-label="Open menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden border-t border-slate-200 bg-white/95 backdrop-blur"
        >
          <div className="px-5 py-4 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="py-2.5 text-base font-medium text-slate-800"
                data-testid={`m-${l.testid}`}
              >
                {l.label}
              </a>
            ))}
            <Button
              asChild
              className="mt-3 rounded-full bg-orange-600 hover:bg-orange-700 text-white"
              data-testid="m-nav-cta-quote"
            >
              <a href="#contact" onClick={() => setOpen(false)}>
                Get free quote
              </a>
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
